import { useRef, useState } from "react";
import L from "lib/leaflet/geoman";
import { useSnackbar } from "notistack";
import { BBox, Feature } from "geojson";

// Project import
import {
  PrintMetaData,
  PrintOptions,
  LegendElement,
  enterPrintMode,
  onPrintLayerExclusionsCallbackProps,
  onPrintLayerTimeoutCallbackProps,
  onPrintImageFailuresCallbackProps,
} from "lib/leaflet/print";
import { useSelector } from "store";
import { selectUiBranding } from "store/reducers/branding";
import LayerController from "lib/leaflet/LayerController";
import useSpeciesData from "./useSpeciesData";
import { getLeafletIconHtml } from "utils/iconsUtils";
import { MapTileLayerProps } from "features/map/types/layers";
import {
  FirestoreFeatureProperties,
  FirestoreLayerLegend,
} from "types/firestoreFeatures";
import {
  applyTilePatchOnce,
  createPaneList,
} from "lib/leaflet/helperFunctions";
import { useTranslation } from "utils/locales/utilityFunctions";
import {
  createDefaultBackgroundLayers,
  getWMSList,
} from "features/map/services/map-layers/layersList";
import useAuth from "./useAuth";
import {
  MAP_ZOOM_DELTA,
  MAP_ZOOM_SNAP,
  PANE_LIST,
} from "features/map/config/leafletConfig";
import { getPrintLegends } from "features/map/utils/getPrintLegends";
import { getRenderedStackFeatures } from "features/map/utils/getRenderedStackFeatures";
import { jsPdfToArrayBufferLike } from "utils/pdfUtils";
import { wait } from "utils/timingFunctions";
import { SnackbarDismissAction } from "components/actions/snackbarActions";
import { LONG_SNACKBAR_DISMISS_TIMEOUT } from "config/config-general";
import { getInternalAppType } from "utils/brandingUtils";
import useCustomCategories from "./useCustomCategories";

// --- Types --- //
type PrintMapProps = {
  printMode: "single" | "dual" | "triple"; // Not used yet
  features?: Feature[];
  bbox?: BBox;
  backgroundLayerId?: string;
  overlayLayersIds?: string[];
  secondaryOverlayLayersIds?: string[]; // Not used in print, just for testing
  printOptions?: Partial<PrintOptions>;
  dataOptions?: {
    transparent?: boolean;
    secondaryTransparent?: boolean;
  };
  meta?: PrintMetaData;
  debug?: boolean;
};

// --- Parameters --- //
const defaultPrintOptions: PrintOptions = {
  enableLegend: true,
  orientation: "Landscape",
  paperSize: "A4",
  screenPercentage: 100,
  scaleRatios: [
    1, 2.5, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000,
    100000,
  ].map((num) => ({
    fromUnit: "CENTIMETER",
    fromLength: 1,
    toUnit: "METER",
    toLength: num,
  })),
  quality: 1.5,
  documentTitle: "default document title",
  lockPrintScale: true,
};

const LAYOUT_BASE_DPI = 96;
const SIDEBAR_WIDTH_MM = 69; // must match print.ts sidebar width
const BOUNDS_PADDING_RATIO = 0.08; // 8% padding around features when fitting bounds
const MARGIN_PX_TUNE = 5; // in mm, converted to px at runtime. This is the margin outside the sidebar and map, tune as needed.
// Pick 2 or 3. Start with 2.
// 2 => ~2244×1588 px (A4 landscape) which is close to many app viewports
const VIEWPORT_SCALE = 1;

const PAPER_MM = {
  A4: { w: 210, h: 297 },
  A3: { w: 297, h: 420 },
  A2: { w: 420, h: 594 },
} as const;

// ==============================|| PRINT MAP - HOOK  ||============================== //
const usePrintMap = () => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { externalWMSLayers, userOrg, user } = useAuth();
  const ui = useSelector(selectUiBranding);
  const { getSpeciesColor, getLineStyle, getSpeciesNameAndAbbreviation } =
    useSpeciesData();
  const { customCategories } = useCustomCategories();

  // --- State --- //
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState<any | null>(null);

  // --- References --- //
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const layerControllerRef = useRef<LayerController | null>(null);
  const cancelRef = useRef<boolean>(false);
  const reportedIncompleteLayersRef = useRef<Set<string>>(new Set());

  // --- Dynamic values --- //
  const iaVersion = getInternalAppType({
    pathOrHost: window.location.host,
  });

  // --- Functions --- //
  function initPrintMap() {
    if (!containerRef.current) {
      const el = document.createElement("div");
      el.id = "print-map-container";
      Object.assign(el.style, {
        position: "fixed",
        top: "0px",
        left: "0px",
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: "0",
      });
      el.setAttribute("aria-hidden", "true");
      document.body.appendChild(el);
      containerRef.current = el;
    }
    if (!mapRef.current && containerRef.current) {
      applyTilePatchOnce();
      mapRef.current = L.map(containerRef.current, {
        zoomDelta: MAP_ZOOM_DELTA,
        zoomSnap: MAP_ZOOM_SNAP,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        dragging: false,
        zoomAnimation: false,
        fadeAnimation: false,
        inertia: false,
      });
      // Create map panes
      createPaneList(mapRef.current, PANE_LIST);

      // Create layer controller
      layerControllerRef.current = new LayerController({
        map: mapRef.current,
        setSelectedFeature: () => {},
        getSpeciesColor,
        getSpeciesNameAndAbbreviation,
        getIcon: getLeafletIconHtml,
        getLineStyle,
      });
      // Add empty layer for features
      layerControllerRef.current.addLayer("print-layer");
    }
  }

  function resetPrintMap() {
    try {
      layerControllerRef.current = null;
      if (mapRef.current) {
        mapRef.current.off(); // remove handlers
        mapRef.current.eachLayer((l) => mapRef.current!.removeLayer(l));
        mapRef.current.remove(); // dispose
      }
    } catch (e) {
      console.warn("Print map remove error", e);
    } finally {
      mapRef.current = null;
    }

    try {
      containerRef.current?.remove();
    } finally {
      containerRef.current = null;
    }
  }

  function getBackgroundLayerById(map: L.Map, id?: string) {
    // Never use defaultBackgroundLayers here: it holds the layer instances
    // mounted on the live map page. A print map must own its own layers.
    const defaultBackgroundLayers = createDefaultBackgroundLayers();
    let newBackgroundLayers: MapTileLayerProps[] = defaultBackgroundLayers;
    if (externalWMSLayers && Object.keys(externalWMSLayers).length > 0) {
      const extBackgroundLayers = getWMSList(
        map,
        externalWMSLayers,
        "background",
        undefined,
        undefined
      );
      newBackgroundLayers = [...newBackgroundLayers, ...extBackgroundLayers];
    }
    if (id) {
      const layer = newBackgroundLayers.find((lay) => lay.id === id);
      if (layer) return layer;
      return defaultBackgroundLayers[0];
    }
    return defaultBackgroundLayers[0];
  }

  function getOverlayLayersByIds(map: L.Map, ids?: string[]) {
    if (!ids || ids.length === 0) return [];
    let newOverlayLayers: MapTileLayerProps[] = [];
    if (externalWMSLayers && Object.keys(externalWMSLayers).length > 0) {
      const extOverlayLayers = getWMSList(
        map,
        externalWMSLayers,
        "overlay",
        undefined,
        undefined
      );
      newOverlayLayers = [...newOverlayLayers, ...extOverlayLayers];
    }
    const layers = newOverlayLayers.filter((lay) => ids.includes(lay.id));
    return layers;
  }

  async function addLayerToPrintMap(layer: MapTileLayerProps) {
    if (!mapRef.current) return;
    return new Promise<void>((resolve) => {
      // layer.leafletObj.once("load", async () => {
      //   resolve();
      // });
      layer.leafletObj.addTo(mapRef.current!);
      resolve();
      // in case 'load' does not fire (cached tile layers)
      // setTimeout(() => {
      //   resolve();
      // }, LAYER_TIMEOUT);
    });
  }

  function addFeaturesToMap(
    features: Feature[],
    buildBbox: boolean
  ): BBox | void {
    if (!layerControllerRef.current) return;
    let newBox: BBox | void = undefined;
    for (const f of features) {
      try {
        layerControllerRef.current.updateFeature({
          layerId: "print-layer",
          feature: f,
        });
        if (buildBbox) {
          const properties = f.properties as FirestoreFeatureProperties;
          const { bbox } = properties;
          if (bbox) {
            if (!newBox) {
              newBox = [...bbox] as BBox;
            } else {
              newBox[0] = Math.min(newBox[0], bbox[0]);
              newBox[1] = Math.min(newBox[1], bbox[1]);
              newBox[2] = Math.max(newBox[2], bbox[2]);
              newBox[3] = Math.max(newBox[3], bbox[3]);
            }
          }
        }
      } catch (error) {
        console.error("Error adding feature to print map", error);
      }
    }

    return newBox;
  }

  function handlePrintLayerExclusions(e: onPrintLayerExclusionsCallbackProps) {
    // Fire snackbar
    if (e.excluded) {
      let layerNames = "";
      e.excluded.forEach((layer, index) => {
        layerNames += layer.label;
        if (index < e.excluded!.length - 1) layerNames += ", ";
      });
      enqueueSnackbar(
        `${translate("print-failed-layers-message")} ${layerNames}`,
        {
          variant: "warning",
          autoHideDuration: LONG_SNACKBAR_DISMISS_TIMEOUT,
          action: (key) => (
            <SnackbarDismissAction id={key} text={translate("close")} />
          ),
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
          },
        }
      );
    }
  }

  function handlePrintLayerTimeouts(e: onPrintLayerTimeoutCallbackProps) {
    const newLayers = e.timedOut.filter(
      (layer) => !reportedIncompleteLayersRef.current.has(layer.label)
    );
    if (newLayers.length === 0) return;
    newLayers.forEach((layer) =>
      reportedIncompleteLayersRef.current.add(layer.label)
    );
    const layerNames = newLayers.map((layer) => layer.label).join(", ");
    enqueueSnackbar(
      `${translate("print-layers-incomplete-message")} ${layerNames}`,
      {
        variant: "warning",
        autoHideDuration: LONG_SNACKBAR_DISMISS_TIMEOUT,
        action: (key) => (
          <SnackbarDismissAction id={key} text={translate("close")} />
        ),
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
        },
      }
    );
  }

  function handlePrintImageFailures({
    layerLabels,
    unmatchedCount,
  }: onPrintImageFailuresCallbackProps) {
    const messages: string[] = [];
    if (layerLabels.length > 0) {
      messages.push(
        `${translate("print-images-skipped-layers-message")} ${layerLabels.join(", ")}`
      );
    }
    if (unmatchedCount > 0) {
      messages.push(
        `${translate("print-images-skipped-unknown-message")} (${unmatchedCount})`
      );
    }
    enqueueSnackbar(messages.join(" "), {
      variant: "warning",
      autoHideDuration: LONG_SNACKBAR_DISMISS_TIMEOUT,
      action: (key) => (
        <SnackbarDismissAction id={key} text={translate("close")} />
      ),
    });
  }

  function handlePrintError(e: any) {
    enqueueSnackbar(translate("snackbar-print-failed"), {
      variant: "error",
      autoHideDuration: LONG_SNACKBAR_DISMISS_TIMEOUT,
      action: (key) => (
        <SnackbarDismissAction id={key} text={translate("close")} />
      ),
    });
    // Throw the error again to stop the print process
    throw e;
  }

  function getPrintOptions(printOptions?: Partial<PrintOptions>): PrintOptions {
    return {
      ...defaultPrintOptions,
      ...printOptions,
      onPrintLayerExclusions: handlePrintLayerExclusions,
      onPrintLayerTimeouts: handlePrintLayerTimeouts,
      onPrintImageFailures: handlePrintImageFailures,
    };
  }

  function getMetaData(meta?: PrintMetaData): PrintMetaData {
    // Check path for company name
    return {
      intlText: {
        title: translate("print-title"),
        map: translate("print-map-name"),
        project: translate("print-project-name"),
        activity: translate("print-activity-name"),
        estate: translate("print-estate-name"),
        timeStamp: translate("print-timestamp"),
        legend: translate("print-legend-title"),
      },
      mapName: undefined,
      projectName: undefined,
      activityName: undefined,
      estateName: undefined,
      printFooter: translate(`print-footer-${iaVersion}`),
      printLogo: ui?.assets.printHeaderLogo?.url,
      ...meta,
    };
  }

  function getOverlayLayersLegend(overlays: MapTileLayerProps[]) {
    let newLengends: { title: string; legends: FirestoreLayerLegend[] }[] = [];
    for (const lay of overlays) {
      if (lay.legend && lay.legend.length > 0) {
        newLengends.push({
          title: lay.label ? lay.label : lay.id,
          legends: lay.legend,
        });
      }
    }
    return newLengends;
  }

  function removeLayersFromPrintMap(layerIds: string[]) {
    if (!mapRef.current) return;
    mapRef.current.eachLayer((layer) => {
      // @ts-expect-error
      if (layer.options?.id && layerIds.includes(layer.options.id)) {
        mapRef.current?.removeLayer(layer);
      }
    });
  }

  function mmToPx(mm: number, dpi: number) {
    return Math.round((mm * dpi) / 25.4);
  }

  function getPaperPx(
    paperSize: keyof typeof PAPER_MM,
    orientation: "Landscape" | "Portrait",
    dpi: number
  ) {
    const { w, h } = PAPER_MM[paperSize];
    const wMm = orientation === "Landscape" ? h : w;
    const hMm = orientation === "Landscape" ? w : h;
    return { widthPx: mmToPx(wMm, dpi), heightPx: mmToPx(hMm, dpi) };
  }

  function ensurePrintViewport(po: PrintOptions): number {
    if (!containerRef.current || !mapRef.current) return LAYOUT_BASE_DPI;

    const layoutDpi = LAYOUT_BASE_DPI * VIEWPORT_SCALE;
    const { widthPx, heightPx } = getPaperPx(
      po.paperSize as any,
      po.orientation,
      layoutDpi
    );

    Object.assign(containerRef.current.style, {
      position: "fixed",
      left: "0px",
      top: "0px",
      width: `${widthPx}px`,
      height: `${heightPx}px`,
      pointerEvents: "none",
      zIndex: "0",
      fontSize: "0.75rem !important",
      lineHeight: "1.5 !important",
      fontFamily: "'Inter', sans-serif !important",
    });

    mapRef.current.invalidateSize({ pan: false });
    return layoutDpi;
  }

  function padBounds(bounds: L.LatLngBounds, ratio = 0.08) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    const latPad = (ne.lat - sw.lat) * ratio;
    const lngPad = (ne.lng - sw.lng) * ratio;

    return L.latLngBounds(
      [sw.lat - latPad, sw.lng - lngPad],
      [ne.lat + latPad, ne.lng + lngPad]
    );
  }

  // Call at the start of each print run
  function beginPrint() {
    setIsPrinting(true);
    setError(null);
    cancelRef.current = false;
    reportedIncompleteLayersRef.current.clear();
  }

  // Call from your “Cancel” button
  function cancelPrint() {
    cancelRef.current = true;
    // best-effort cleanup
    resetPrintMap();
    setIsPrinting(false);
  }

  // Tiny guard
  function ensureNotCancelled() {
    if (cancelRef.current) throw new Error("print-map-cancelled");
  }

  async function printMap({
    printMode,
    features,
    bbox,
    backgroundLayerId,
    overlayLayersIds,
    secondaryOverlayLayersIds,
    printOptions,
    dataOptions,
    meta,
    debug,
  }: PrintMapProps): Promise<ArrayBufferLike[] | string> {
    // --- Start printing process --- //
    beginPrint();
    // --- Initialize map --- //
    try {
      initPrintMap();
      if (!mapRef.current || !layerControllerRef.current) {
        throw new Error("print-map-no-map");
      }
      ensureNotCancelled();
      // Variables
      let mode: "light" | "dark" = "light";
      let transparent = false;
      let printResults: ArrayBufferLike[] = [];
      let overlayLegends: { title: string; legends: FirestoreLayerLegend[] }[] =
        [];
      let legElements: LegendElement[] = [];

      // --- Add features to map --- //
      let newBbox = bbox;
      const buildBbox = bbox ? false : true;
      if (features && features.length > 0) {
        const sortedFeatuers = [...features].sort((a, b) => {
          const pa = a.properties as FirestoreFeatureProperties;
          const pb = b.properties as FirestoreFeatureProperties;
          // Sort according to origin feature (if any)
          if (pa.originFeature && !pb.originFeature) return -1;
          if (!pa.originFeature && pb.originFeature) return 1;
          return 0;
        });
        ensureNotCancelled();
        const featureBbox = addFeaturesToMap(sortedFeatuers, buildBbox);
        if (featureBbox && buildBbox) newBbox = featureBbox;
      }
      if (!newBbox) {
        throw new Error("print-map-no-bbox");
      }

      // --- Set map view --- //
      ensureNotCancelled();
      const po = getPrintOptions(printOptions);
      const layoutDpi = ensurePrintViewport(po);
      const rawBounds = L.latLngBounds(
        [newBbox[1], newBbox[0]],
        [newBbox[3], newBbox[2]]
      );
      const paddedBounds = padBounds(rawBounds, BOUNDS_PADDING_RATIO);
      const sidebarPx = po.enableLegend
        ? mmToPx(SIDEBAR_WIDTH_MM, layoutDpi)
        : 0;
      const marginPx = mmToPx(MARGIN_PX_TUNE, layoutDpi);
      mapRef.current.fitBounds(paddedBounds, {
        animate: false,
        paddingTopLeft: L.point(sidebarPx + marginPx, marginPx),
        paddingBottomRight: L.point(marginPx, marginPx),
      });

      await new Promise<void>((resolve) => {
        mapRef.current!.once("moveend", () => resolve());
        // safety: if moveend doesn't fire (cached/instant), resolve next frame
        requestAnimationFrame(() => resolve());
      });

      ensureNotCancelled();
      if (debug) {
        L.polygon([
          [newBbox[1], newBbox[0]],
          [newBbox[1], newBbox[2]],
          [newBbox[3], newBbox[2]],
          [newBbox[3], newBbox[0]],
        ]).addTo(mapRef.current);
      }
      ensureNotCancelled();
      const pm = enterPrintMode(mapRef.current, po);
      const currentScale = pm.redraw();
      // --- Add background layer to map --- //
      if (backgroundLayerId) {
        const backgroundLayer = getBackgroundLayerById(
          mapRef.current,
          backgroundLayerId
        );
        if (backgroundLayer) {
          if (backgroundLayer.mode) mode = backgroundLayer.mode;
          ensureNotCancelled();
          await addLayerToPrintMap(backgroundLayer);
        }
      }
      // --- If print mode is triple make first print with only features and background --- //
      if (printMode === "triple") {
        legElements = getPrintLegends({
          renderedStackFeatures: getRenderedStackFeatures(mapRef.current!, pm.printArea.main.getBounds()),
          features,
          transparent,
          mode,
          activeCategories: null,
          filterIds: null,
          categoriesFilterLogicType: "OR",
          customCategories,
          externalLayersLegend: [],
          translate,
          getSpeciesColor,
          getLineStyle,
          getSpeciesNameAndAbbreviation,
        });
        ensureNotCancelled();
        let pres = null;
        try {
          pres = await pm.print(
            legElements,
            currentScale,
            getMetaData(meta),
            true
          );
        } catch (e) {
          handlePrintError(e);
        }
        if (pres) printResults.push(jsPdfToArrayBufferLike(pres));
      }
      // --- Setup layer controller for the next print --- //
      if (dataOptions) {
        if (dataOptions.transparent) {
          transparent = true;
          layerControllerRef.current.setTransparentMode(true);
        }
      }

      // --- Add overlay layers to map --- //
      if (overlayLayersIds && overlayLayersIds.length > 0) {
        const overlayLayers = getOverlayLayersByIds(
          mapRef.current,
          overlayLayersIds
        );
        if (overlayLayers && overlayLayers.length > 0) {
          ensureNotCancelled();
          await Promise.all(overlayLayers.map(addLayerToPrintMap));
          overlayLegends = getOverlayLayersLegend(overlayLayers);
        }
      }

      // --- Create legend data --- //
      legElements = getPrintLegends({
        renderedStackFeatures: getRenderedStackFeatures(mapRef.current!, pm.printArea.main.getBounds()),
        features,
        transparent,
        mode,
        activeCategories: null,
        filterIds: null,
        categoriesFilterLogicType: "OR",
        customCategories,
        externalLayersLegend: overlayLegends,
        translate,
        getSpeciesColor,
        getLineStyle,
        getSpeciesNameAndAbbreviation,
      });

      // --- Secondary print with overlays --- //
      ensureNotCancelled();
      let pres = null;
      try {
        pres = await pm.print(
          legElements,
          currentScale,
          getMetaData(meta),
          true
        );
      } catch (e) {
        handlePrintError(e);
      }
      if (pres) printResults.push(jsPdfToArrayBufferLike(pres));
      // --- Check for secondary overlay layers and create print with only these overlays --- //
      // This is a bit of a hacky way to do it, but due to the way the print function works
      // we need to re-add all layers and features and then call the print function again
      // if we want a second map with different overlays.
      // A better solution would be to modify the print function to allow for multiple maps
      // e.g., we need to remove the inserted overlays
      if (secondaryOverlayLayersIds && secondaryOverlayLayersIds.length > 0) {
        // Check for secondary transparent mode
        if (dataOptions) {
          if (dataOptions.secondaryTransparent) {
            transparent = true;
            layerControllerRef.current.setTransparentMode(true);
          }
        }
        let secondaryOverlayLegends: {
          title: string;
          legends: FirestoreLayerLegend[];
        }[] = [];
        // Remove all overlay layers
        ensureNotCancelled();
        removeLayersFromPrintMap(overlayLayersIds ? overlayLayersIds : []);
        // Add secondary overlay layers
        const secondaryOverlayLayers = getOverlayLayersByIds(
          mapRef.current,
          secondaryOverlayLayersIds
        );

        if (secondaryOverlayLayers && secondaryOverlayLayers.length > 0) {
          ensureNotCancelled();
          await Promise.all(secondaryOverlayLayers.map(addLayerToPrintMap));
          secondaryOverlayLegends = getOverlayLayersLegend(
            secondaryOverlayLayers
          );
        }
        // Rebuild legend data
        legElements = getPrintLegends({
          renderedStackFeatures: getRenderedStackFeatures(mapRef.current!, pm.printArea.main.getBounds()),
          features,
          transparent,
          mode,
          activeCategories: null,
          filterIds: null,
          categoriesFilterLogicType: "OR",
          customCategories,
          externalLayersLegend: secondaryOverlayLegends,
          translate,
          getSpeciesColor,
          getLineStyle,
          getSpeciesNameAndAbbreviation,
        });
        ensureNotCancelled();
        let pres = null;
        try {
          pres = await pm.print(
            legElements,
            currentScale,
            getMetaData(meta),
            true
          );
        } catch (e) {
          handlePrintError(e);
        }
        if (pres) printResults.push(jsPdfToArrayBufferLike(pres));
      }

      // --- END PRINT PROCESS --- //
      ensureNotCancelled();
      if (debug) await wait(5000);
      resetPrintMap();
      setIsPrinting(false);
      return printResults;
    } catch (e: any) {
      setError(e);
      return e.message;
    } finally {
      // always clean up (also runs on cancel)
      resetPrintMap();
      setIsPrinting(false);
    }
  }

  return { isPrinting, error, printMap, cancelPrint };
};

export default usePrintMap;
